import { defineStore } from 'pinia'
import { useSnackbarStore } from '@/stores/snackbar'
import {
  login as loginRequest,
  signup as signupRequest,
  validateMobile as validateMobileRequest,
  sendOtp as sendOtpRequest,
  verifyOtp as verifyOtpRequest,
  forgotPassword as forgotPasswordRequest,
  signout as signoutRequest,
  fetchMe as fetchMeRequest,
  validateToken as validateTokenRequest
} from '@/services/auth/authService'
import {
  OTP_POLICY,
  validatePhoneNumber,
  validateOtpCode,
  validateSignupFields,
  validateForgotPasswordFields,
  extractMobileDigits
} from '@/utils/authValidation'

const SESSION_STORAGE_KEY = 'sanwaria_auth_session_v1'
const DEFAULT_MODE = 'signin'
const MODE_VALUES = new Set(['signin', 'signup', 'forgot-password'])

const emptySignupDraft = () => ({
  fullName: '',
  mobile: '',
  email: '',
  password: '',
  passwordConfirmation: ''
})

const emptySignupOtp = () => ({
  verifyId: '',
  otp: '',
  expiresAt: '',
  resendAt: '',
  attemptsLeft: OTP_POLICY.maxAttempts,
  mockOtpHint: ''
})

const emptyForgotDraft = () => ({
  mobile: '',
  otp: '',
  verifyId: '',
  password: '',
  passwordConfirmation: '',
  expiresAt: '',
  resendAt: '',
  attemptsLeft: OTP_POLICY.maxAttempts,
  mockOtpHint: '',
  step: 'mobile' // 'mobile' | 'otp' | 'reset'
})

const getNow = () => Date.now()

const loadSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === 'object') {
      return parsed
    }
  } catch {
    // Ignore corrupted local data and start fresh.
  }

  return null
}

const persistSession = (session) => {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
}

const clearPersistedSession = () => {
  localStorage.removeItem(SESSION_STORAGE_KEY)
}

/**
 * Map a V2 API auth response to a normalized session object.
 *
 * V2 returns:
 * {
 *   data: { user_id, full_name, mobile, token, ... }
 * }
 */
const mapAuthResponseToSession = (response) => {
  const data = response?.data || response

  return {
    token: data?.token || '',
    user: {
      userId: data?.user_id || data?.userId || '',
      fullName: data?.full_name || data?.fullName || '',
      email: data?.email || '',
      mobile: data?.mobile || '',
      callingCode: data?.callingcode || data?.callingCode || '+91',
      type: data?.type || 'guest',
      gender: data?.gender || '',
      profilePic: data?.profile_pic || ''
    },
    createdAt: new Date().toISOString()
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    // --- Mode & flow ---
    mode: DEFAULT_MODE, // 'signin' | 'signup' | 'forgot-password'
    step: 'credentials', // signin: 'credentials' | signup: 'details' | forgot: via forgotDraft.step
    status: 'idle',

    // --- Login fields ---
    loginMobile: '',
    loginPassword: '',

    // --- Signup details ---
    signupDraft: emptySignupDraft(),
    signupOtp: emptySignupOtp(),

    // --- Forgot password ---
    forgotDraft: emptyForgotDraft(),

    // --- Session & status ---
    session: null,
    error: null,
    signupSuccess: false,
    nowTs: getNow()
  }),

  getters: {
    isAuthenticated: (state) => Boolean(state.session?.token && state.session?.user),

    currentUser: (state) => state.session?.user || null,

    // Forgot-password OTP timers
    isForgotOtpExpired: (state) => {
      if (!state.forgotDraft.expiresAt) return false
      return new Date(state.forgotDraft.expiresAt).getTime() <= state.nowTs
    },

    canResendForgotOtp: (state) => {
      if (!state.forgotDraft.verifyId || !state.forgotDraft.resendAt) return false
      return new Date(state.forgotDraft.resendAt).getTime() <= state.nowTs
    },

    forgotOtpCountdownSeconds: (state) => {
      if (!state.forgotDraft.expiresAt) return 0
      const remaining = new Date(state.forgotDraft.expiresAt).getTime() - state.nowTs
      return remaining > 0 ? Math.ceil(remaining / 1000) : 0
    },

    forgotResendCountdownSeconds: (state) => {
      if (!state.forgotDraft.resendAt) return 0
      const remaining = new Date(state.forgotDraft.resendAt).getTime() - state.nowTs
      return remaining > 0 ? Math.ceil(remaining / 1000) : 0
    },

    // Signup OTP timers
    isSignupOtpExpired: (state) => {
      if (!state.signupOtp.expiresAt) return false
      return new Date(state.signupOtp.expiresAt).getTime() <= state.nowTs
    },

    canResendSignupOtp: (state) => {
      if (!state.signupOtp.verifyId || !state.signupOtp.resendAt) return false
      return new Date(state.signupOtp.resendAt).getTime() <= state.nowTs
    },

    signupOtpCountdownSeconds: (state) => {
      if (!state.signupOtp.expiresAt) return 0
      const remaining = new Date(state.signupOtp.expiresAt).getTime() - state.nowTs
      return remaining > 0 ? Math.ceil(remaining / 1000) : 0
    },

    signupResendCountdownSeconds: (state) => {
      if (!state.signupOtp.resendAt) return 0
      const remaining = new Date(state.signupOtp.resendAt).getTime() - state.nowTs
      return remaining > 0 ? Math.ceil(remaining / 1000) : 0
    }
  },

  actions: {
    refreshClock() {
      this.nowTs = getNow()
    },

    setError(error) {
      this.error = {
        code: String(error?.code || 'UNKNOWN'),
        message: String(error?.message || 'Something went wrong. Please try again.'),
        meta: error?.meta || null
      }

      const snackbar = useSnackbarStore()
      snackbar.error(this.error.message)
    },

    setMode(mode) {
      const nextMode = MODE_VALUES.has(mode) ? mode : DEFAULT_MODE

      if (this.mode === nextMode) return

      this.mode = nextMode
      this.signupSuccess = false
      this.error = null

      if (nextMode === 'signin') {
        this.step = 'credentials'
        this._resetSignupFlow()
        this.forgotDraft = emptyForgotDraft()
      } else if (nextMode === 'signup') {
        this.step = 'details'
        this.loginMobile = ''
        this.loginPassword = ''
        this.forgotDraft = emptyForgotDraft()
      } else if (nextMode === 'forgot-password') {
        this.step = 'forgot'
        this.loginMobile = ''
        this.loginPassword = ''
        this._resetSignupFlow()
        this.forgotDraft = emptyForgotDraft()
      }
    },

    // ─── LOGIN (password) ────────────────────────────────────────────

    async login({ mobile, password } = {}) {
      const phoneInput = mobile || this.loginMobile
      const passwordInput = password || this.loginPassword

      const phoneValidation = validatePhoneNumber(phoneInput)
      if (!phoneValidation.valid) {
        this.status = 'error'
        this.setError(phoneValidation)
        return null
      }

      if (!passwordInput) {
        this.status = 'error'
        this.setError({ code: 'VALIDATION_ERROR', message: 'Password is required.' })
        return null
      }

      this.status = 'logging_in'
      this.error = null

      const snackbarLogin = useSnackbarStore()
      snackbarLogin.info('Logging in...')

      try {
        const mobileDigits = extractMobileDigits(phoneInput)
        const response = await loginRequest({ mobile: mobileDigits, password: passwordInput })
        const session = mapAuthResponseToSession(response)

        this.session = session
        persistSession(session)
        this.status = 'authenticated'

        const snackbar = useSnackbarStore()
        snackbar.success('Logged in successfully')

        // Fetch full user profile in the background (sign-in only returns token)
        this.fetchProfile()

        return session
      } catch (error) {
        this.status = 'error'
        this.setError(error)
        return null
      }
    },

    // ─── SIGNUP Step 1: Validate fields + mobile → send OTP ─────────

    async signupValidateAndSendOtp(draft = this.signupDraft) {
      const validation = validateSignupFields(draft)
      if (!validation.valid) {
        this.status = 'error'
        this.setError({
          code: 'VALIDATION_ERROR',
          message: 'Please fix the errors below.',
          meta: { fields: validation.errors }
        })
        return null
      }

      this.signupDraft = { ...draft }
      this.status = 'validating_mobile'
      this.error = null

      const snackbar = useSnackbarStore()
      snackbar.info('Validating mobile number...')

      const mobileDigits = extractMobileDigits(draft.mobile)

      try {
        // Step 1a: Validate mobile (check duplicates, etc.)
        await validateMobileRequest({
          mobile: mobileDigits,
          source: 'SIGNUP',
          fullName: String(draft.fullName ?? '').trim(),
          email: String(draft.email ?? '').trim(),
          password: draft.password,
          passwordConfirmation: draft.passwordConfirmation
        })
      } catch (error) {
        this.status = 'error'
        this.setError(error)
        return null
      }

      // Step 1b: Send OTP
      this.status = 'sending_otp'
      snackbar.info('Sending OTP...')

      try {
        const response = await sendOtpRequest({ mobile: mobileDigits })
        const data = response?.data || response

        this.signupOtp.verifyId = String(data?.verify_id || data?.verifyId || '')

        const expiresRaw = data?.expires_at || data?.expiresAt || ''
        this.signupOtp.expiresAt = expiresRaw
          ? new Date(expiresRaw + (expiresRaw.includes('T') ? '' : 'Z')).toISOString()
          : ''

        const resendRaw = data?.resend_at || data?.resendAt || ''
        if (resendRaw) {
          this.signupOtp.resendAt = new Date(resendRaw + (resendRaw.includes('T') ? '' : 'Z')).toISOString()
        } else {
          this.signupOtp.resendAt = new Date(Date.now() + OTP_POLICY.resendCooldownSeconds * 1000).toISOString()
        }

        this.signupOtp.attemptsLeft = Number(data?.attemptsLeft ?? data?.attempts_left ?? OTP_POLICY.maxAttempts)
        this.signupOtp.mockOtpHint = String(data?.mockOtp || data?.mock_otp || '')
        this.signupOtp.otp = ''
        this.step = 'signup-otp'
        this.status = 'otp_sent'

        snackbar.success('OTP sent to your mobile number')
        return response
      } catch (error) {
        this.status = 'error'
        this.setError(error)
        return null
      }
    },

    // ─── SIGNUP Step 2: Resend OTP ───────────────────────────────────

    async signupResendOtp() {
      this.refreshClock()

      if (!this.canResendSignupOtp) {
        this.setError({
          code: 'VALIDATION_ERROR',
          message: 'Please wait before requesting another OTP.'
        })
        return null
      }

      this.status = 'sending_otp'
      this.error = null

      const snackbar = useSnackbarStore()
      snackbar.info('Resending OTP...')

      const mobileDigits = extractMobileDigits(this.signupDraft.mobile)

      try {
        const response = await sendOtpRequest({ mobile: mobileDigits })
        const data = response?.data || response

        this.signupOtp.verifyId = String(data?.verify_id || data?.verifyId || '')

        const expiresRaw = data?.expires_at || data?.expiresAt || ''
        this.signupOtp.expiresAt = expiresRaw
          ? new Date(expiresRaw + (expiresRaw.includes('T') ? '' : 'Z')).toISOString()
          : ''

        const resendRaw = data?.resend_at || data?.resendAt || ''
        if (resendRaw) {
          this.signupOtp.resendAt = new Date(resendRaw + (resendRaw.includes('T') ? '' : 'Z')).toISOString()
        } else {
          this.signupOtp.resendAt = new Date(Date.now() + OTP_POLICY.resendCooldownSeconds * 1000).toISOString()
        }

        this.signupOtp.attemptsLeft = Number(data?.attemptsLeft ?? data?.attempts_left ?? OTP_POLICY.maxAttempts)
        this.signupOtp.mockOtpHint = String(data?.mockOtp || data?.mock_otp || '')
        this.signupOtp.otp = ''
        this.status = 'otp_sent'

        snackbar.success('OTP resent to your mobile number')
        return response
      } catch (error) {
        this.status = 'error'
        this.setError(error)
        return null
      }
    },

    // ─── SIGNUP Step 3: Verify OTP → call signup API ─────────────────

    async signupVerifyOtpAndCreate() {
      this.refreshClock()

      const otpValidation = validateOtpCode(this.signupOtp.otp)
      if (!otpValidation.valid) {
        this.status = 'error'
        this.setError(otpValidation)
        return null
      }

      if (!this.signupOtp.verifyId) {
        this.status = 'error'
        this.setError({ code: 'VALIDATION_ERROR', message: 'OTP session expired. Please go back and try again.' })
        return null
      }

      this.status = 'verifying_otp'
      this.error = null

      const snackbar = useSnackbarStore()
      snackbar.info('Verifying OTP...')

      const mobileDigits = extractMobileDigits(this.signupDraft.mobile)

      try {
        // Step 3a: Verify OTP
        await verifyOtpRequest({
          mobile: mobileDigits,
          otp: this.signupOtp.otp,
          verifyId: this.signupOtp.verifyId
        })
      } catch (error) {
        this.signupOtp.attemptsLeft = Math.max(0, this.signupOtp.attemptsLeft - 1)
        this.status = 'error'
        this.setError(error)
        return null
      }

      // Step 3b: Create account
      this.status = 'signing_up'
      snackbar.info('Creating account...')

      try {
        const draft = this.signupDraft
        const response = await signupRequest({
          mobile: mobileDigits,
          fullName: String(draft.fullName ?? '').trim(),
          email: String(draft.email ?? '').trim(),
          password: draft.password,
          passwordConfirmation: draft.passwordConfirmation
        })

        const session = mapAuthResponseToSession(response)
        this.session = session
        persistSession(session)
        this.status = 'authenticated'

        snackbar.success('Account created successfully!')

        // Fetch full user profile in the background
        this.fetchProfile()

        return session
      } catch (error) {
        this.status = 'error'
        this.setError(error)
        return null
      }
    },

    // ─── FORGOT PASSWORD: Step 1 — Send OTP ─────────────────────────

    async sendForgotOtp(mobile) {
      this.refreshClock()

      const phoneInput = mobile || this.forgotDraft.mobile
      const phoneValidation = validatePhoneNumber(phoneInput)

      if (!phoneValidation.valid) {
        this.status = 'error'
        this.setError(phoneValidation)
        return null
      }

      this.forgotDraft.mobile = phoneInput
      this.status = 'sending_otp'
      this.error = null

      const snackbar = useSnackbarStore()
      snackbar.info('Sending OTP...')

      try {
        const mobileDigits = extractMobileDigits(phoneInput)
        const response = await sendOtpRequest({ mobile: mobileDigits })

        const data = response?.data || response
        this.forgotDraft.verifyId = String(data?.verify_id || data?.verifyId || '')

        const expiresRaw = data?.expires_at || data?.expiresAt || ''
        this.forgotDraft.expiresAt = expiresRaw
          ? new Date(expiresRaw + (expiresRaw.includes('T') ? '' : 'Z')).toISOString()
          : ''

        const resendRaw = data?.resend_at || data?.resendAt || ''
        if (resendRaw) {
          this.forgotDraft.resendAt = new Date(resendRaw + (resendRaw.includes('T') ? '' : 'Z')).toISOString()
        } else {
          this.forgotDraft.resendAt = new Date(Date.now() + OTP_POLICY.resendCooldownSeconds * 1000).toISOString()
        }

        this.forgotDraft.attemptsLeft = Number(data?.attemptsLeft ?? data?.attempts_left ?? OTP_POLICY.maxAttempts)
        this.forgotDraft.mockOtpHint = String(data?.mockOtp || data?.mock_otp || '')
        this.forgotDraft.otp = ''
        this.forgotDraft.step = 'otp'
        this.status = 'otp_sent'

        snackbar.success('OTP sent to your mobile number')

        return response
      } catch (error) {
        this.status = 'error'
        this.setError(error)
        return null
      }
    },

    // ─── FORGOT PASSWORD: Step 2 — Resend OTP ───────────────────────

    async resendForgotOtp() {
      this.refreshClock()

      if (!this.canResendForgotOtp) {
        this.setError({
          code: 'VALIDATION_ERROR',
          message: 'Please wait before requesting another OTP.'
        })
        return null
      }

      const snackbar = useSnackbarStore()
      snackbar.info('Resending OTP...')

      return this.sendForgotOtp(this.forgotDraft.mobile)
    },

    // ─── FORGOT PASSWORD: Step 3 — Verify OTP + Reset Password ──────

    async resetPassword() {
      this.refreshClock()

      const { otp, password, passwordConfirmation } = this.forgotDraft

      const validation = validateForgotPasswordFields({ otp, password, passwordConfirmation })
      if (!validation.valid) {
        this.status = 'error'
        this.setError({
          code: 'VALIDATION_ERROR',
          message: 'Please fix the errors below.',
          meta: { fields: validation.errors }
        })
        return null
      }

      if (!this.forgotDraft.verifyId) {
        this.status = 'error'
        this.setError({ code: 'VALIDATION_ERROR', message: 'Request OTP before resetting password.' })
        return null
      }

      this.status = 'resetting_password'
      this.error = null

      const snackbar = useSnackbarStore()
      snackbar.info('Resetting password...')

      try {
        const mobileDigits = extractMobileDigits(this.forgotDraft.mobile)
        await forgotPasswordRequest({
          mobile: mobileDigits,
          otp: this.forgotDraft.otp,
          verifyId: this.forgotDraft.verifyId,
          password: this.forgotDraft.password,
          passwordConfirmation: this.forgotDraft.passwordConfirmation
        })

        // Password reset successful — switch to login
        this.loginMobile = mobileDigits
        this.loginPassword = ''
        this.setMode('signin')
        this.status = 'idle'

        snackbar.success('Password reset successfully. Please log in with your new password.')

        return true
      } catch (error) {
        this.status = 'error'
        this.setError(error)
        return null
      }
    },

    // ─── Session management ──────────────────────────────────────────

    async signOut() {
      // Best-effort API signout (invalidate server-side token)
      try {
        if (this.session?.token) {
          await signoutRequest()
        }
      } catch {
        // Ignore — we still clear the local session regardless
      }

      this.session = null
      clearPersistedSession()
      this.mode = DEFAULT_MODE
      this.step = 'credentials'
      this.status = 'idle'
      this.loginMobile = ''
      this.loginPassword = ''
      this.signupSuccess = false
      this._resetSignupFlow()
      this.forgotDraft = emptyForgotDraft()

      const snackbar = useSnackbarStore()
      snackbar.info('You have been logged out')
    },

    hydrateSession() {
      this.session = loadSession()
      this.refreshClock()

      // If we have a token but user profile is incomplete, fetch it
      if (this.session?.token && !this.session?.user?.fullName) {
        this.fetchProfile()
      }

      return this.session
    },

    /**
     * Fetch user profile via GET /auth/me and merge into the current session.
     * Called after login/signup since sign-in only returns a token.
     */
    async fetchProfile() {
      if (!this.session?.token) return

      try {
        const response = await fetchMeRequest()
        const data = response?.data || response

        if (data && this.session) {
          const user = this.session.user || {}
          this.session = {
            ...this.session,
            user: {
              ...user,
              userId: data.user_id || data.userId || user.userId || '',
              fullName: data.full_name || data.fullName || user.fullName || '',
              email: data.email || user.email || '',
              mobile: data.mobile || user.mobile || '',
              callingCode: data.callingcode || data.callingCode || user.callingCode || '+91',
              type: data.type || user.type || 'guest',
              gender: data.gender || user.gender || '',
              profilePic: data.profile_pic || user.profilePic || ''
            }
          }
          persistSession(this.session)
        }
      } catch {
        // Profile fetch is best-effort; don't break the auth flow
      }
    },

    /**
     * Validate an SSO token from the management app.
     * Called during boot when ?sso_token=xxx is present in URL.
     *
     * POST /auth/validate-token → returns fresh OBE token + user info
     *
     * @param {string} token - JWT token from management app
     * @returns {Promise<boolean>} - true if validation succeeded and user is logged in
     */
    async validateSsoToken(token) {
      if (!token) return false

      try {
        const response = await validateTokenRequest({ token, source: 'MANAGEMENT' })
        const data = response?.data || response

        if (data?.token) {
          // Build session from validate-token response
          this.session = {
            token: data.token,
            user: {
              userId: data.user_id || '',
              fullName: data.full_name || '',
              email: data.email || '',
              mobile: data.mobile || '',
              callingCode: data.callingcode || '+91',
              type: 'guest',
              gender: '',
              profilePic: ''
            }
          }
          persistSession(this.session)

          // Fetch full profile in background
          this.fetchProfile()

          return true
        }
      } catch (err) {
        // SSO validation failed — user will continue as guest
        console.warn('SSO token validation failed:', err?.message || err)
      }

      return false
    },

    clearFlow(options = {}) {
      const keepMode = Boolean(options.keepMode)
      const keepPhone = Boolean(options.keepPhone)
      const currentMode = this.mode
      const currentPhone = this.forgotDraft.mobile

      this.mode = keepMode ? currentMode : DEFAULT_MODE
      this.step = this.mode === 'signin' ? 'credentials' : 'details'
      this.status = 'idle'
      this.loginMobile = ''
      this.loginPassword = ''
      this.signupSuccess = false
      this.error = null
      this._resetSignupFlow()
      this.forgotDraft = keepPhone
        ? { ...emptyForgotDraft(), mobile: currentPhone }
        : emptyForgotDraft()
      this.refreshClock()
    },

    _resetSignupFlow() {
      this.signupDraft = emptySignupDraft()
      this.signupOtp = emptySignupOtp()
    }
  }
})
